import { Form, Input, InputNumber, Row, Col, Segmented, Checkbox } from 'antd';
import type { ScheduleStructured } from '@/types/topic';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface Props {
  value: ScheduleStructured;
  onChange: (next: ScheduleStructured) => void;
}

export function ScheduleForm({ value, onChange }: Props) {
  const set = <K extends keyof ScheduleStructured>(k: K, v: ScheduleStructured[K]) =>
    onChange({ ...value, [k]: v });
  const full = { width: '100%' } as const;

  return (
    <Form layout="vertical">
      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Form.Item label="Date & time" style={{ marginBottom: 12 }}>
            <Input
              value={value.schedule_datetime ?? ''}
              onChange={(e) => set('schedule_datetime', e.target.value || null)}
              placeholder="e.g. 2026-07-15 14:00 PT"
            />
          </Form.Item>
        </Col>
        <Col xs={12} md={6}>
          <Form.Item label="Session #" style={{ marginBottom: 12 }}>
            <InputNumber
              style={full}
              min={1}
              value={value.session_number ?? undefined}
              onChange={(v) => set('session_number', (v as number | null) ?? null)}
            />
          </Form.Item>
        </Col>
        <Col xs={12} md={6}>
          <Form.Item label="Location" style={{ marginBottom: 12 }}>
            <Input value={value.location ?? ''} onChange={(e) => set('location', e.target.value || null)} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col xs={24} md={16}>
          <Form.Item label="Meeting link" style={{ marginBottom: 12 }}>
            <Input
              value={value.meeting_link ?? ''}
              onChange={(e) => set('meeting_link', e.target.value || null)}
              placeholder="https://…"
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Presenter" style={{ marginBottom: 12 }}>
            <Input value={value.presenter ?? ''} onChange={(e) => set('presenter', e.target.value || null)} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item label="Frequency" style={{ marginBottom: 12 }}>
        <Segmented
          value={value.frequency ?? 'once'}
          onChange={(v) => set('frequency', v as 'once' | 'weekly')}
          options={[{ label: 'Once', value: 'once' }, { label: 'Weekly', value: 'weekly' }]}
        />
      </Form.Item>
      {value.frequency === 'weekly' && (
        <Form.Item label="Weekdays" style={{ marginBottom: 12 }}>
          <Checkbox.Group
            value={value.weekdays}
            onChange={(v) => set('weekdays', v as string[])}
            options={WEEKDAYS.map((d) => ({ label: d, value: d }))}
          />
        </Form.Item>
      )}
      <Form.Item label="Calendar (.ics) URL" style={{ marginBottom: 12 }}>
        <Input
          value={value.ics_url ?? ''}
          onChange={(e) => set('ics_url', e.target.value || null)}
          placeholder="https://…/event.ics"
        />
      </Form.Item>
      <Form.Item label="Notes" style={{ marginBottom: 0 }}>
        <Input.TextArea
          rows={3}
          value={value.notes ?? ''}
          onChange={(e) => set('notes', e.target.value || null)}
        />
      </Form.Item>
    </Form>
  );
}
